import { createContext, useContext, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import type { ChampTexte, FicheSalarie } from './ficheSalarie';

/**
 * Briques du questionnaire. Elles lisent et écrivent la fiche par un contexte :
 * le formulaire compte plus de cent questions, et passer `valeur/onChange` à
 * chacune noierait ce qui compte — la retranscription fidèle du papier.
 */
interface Ctx {
  d: FicheSalarie;
  set: <K extends keyof FicheSalarie>(k: K, v: FicheSalarie[K]) => void;
}
export const FicheCtx = createContext<Ctx | null>(null);

export function useFiche(): Ctx {
  const c = useContext(FicheCtx);
  if (!c) throw new Error('useFiche hors de FicheCtx');
  return c;
}

/** Bandeau vert d'une grande partie, comme sur la fiche papier. */
export function Bandeau({ id, children, sous }: { id?: string; children: ReactNode; sous?: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-2.5">
      <div className="text-center text-[12.5px] font-bold uppercase tracking-[0.14em]">{children}</div>
      {sous && <div className="text-center text-[11px] text-white/85 mt-0.5">{sous}</div>}
    </div>
  );
}

/** Rubrique à l'intérieur d'une partie (« Identité », « Localisation »…). */
export function Rubrique({ titre, note, children }: { titre?: ReactNode; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="px-5 py-4 border-t border-slate-200 first:border-t-0 space-y-3">
      {titre && (
        <div className="text-[13px] font-semibold text-slate-900">
          {titre} {note && <span className="font-normal italic text-slate-500">{note}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

export function Ligne({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${className}`}>{children}</div>;
}

export function Question({ children }: { children: ReactNode }) {
  return <span className="text-[13px] text-slate-800">{children}</span>;
}

const inputCls =
  'text-[13px] border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/15 placeholder:text-slate-400';

/** Champ texte libellé. `k` = la question de la fiche qu'il remplit. */
export function Texte({
  k,
  label,
  type = 'text',
  placeholder,
  className = 'flex-1 min-w-[180px]',
  suffixe,
  inputMode,
}: {
  k: ChampTexte;
  label?: ReactNode;
  type?: 'text' | 'date' | 'email';
  placeholder?: string;
  className?: string;
  suffixe?: string;
  inputMode?: 'numeric' | 'decimal' | 'email' | 'text';
}) {
  const { d, set } = useFiche();
  return (
    <label className={`flex items-center gap-2 ${className}`}>
      {label && <span className="text-[13px] text-slate-700 whitespace-nowrap">{label}</span>}
      <span className="relative flex-1 min-w-0">
        <input
          type={type}
          value={d[k]}
          inputMode={inputMode}
          placeholder={placeholder}
          onChange={(e) => set(k, e.target.value)}
          className={`${inputCls} w-full ${suffixe ? 'pr-7' : ''}`}
        />
        {suffixe && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-500 pointer-events-none">
            {suffixe}
          </span>
        )}
      </span>
    </label>
  );
}

/** Champ texte hors fiche (sous-objets : périodes, créneaux). */
export function TexteLibre({
  value,
  onChange,
  label,
  type = 'text',
  placeholder,
  className = 'flex-1 min-w-[140px]',
}: {
  value: string;
  onChange: (v: string) => void;
  label?: ReactNode;
  type?: 'text' | 'date';
  placeholder?: string;
  className?: string;
}) {
  return (
    <label className={`flex items-center gap-2 ${className}`}>
      {label && <span className="text-[13px] text-slate-700 whitespace-nowrap">{label}</span>}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} w-full min-w-0`}
      />
    </label>
  );
}

export function Zone({ k, lignes = 3, placeholder }: { k: ChampTexte; lignes?: number; placeholder?: string }) {
  const { d, set } = useFiche();
  return (
    <textarea
      value={d[k]}
      rows={lignes}
      placeholder={placeholder}
      onChange={(e) => set(k, e.target.value)}
      className={`${inputCls} w-full resize-y leading-relaxed`}
    />
  );
}

/** La case à cocher du papier. Recliquer une case cochée la décoche. */
export function Case({
  on,
  onClick,
  children,
  fort,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  fort?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
        fort ? 'text-[14px] font-semibold' : 'text-[13px]'
      } ${
        on
          ? 'border-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm'
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
      }`}
    >
      <span
        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
          on ? 'border-emerald-600 bg-emerald-600' : 'border-slate-400 bg-white'
        }`}
      >
        {on && <Check size={11} strokeWidth={3.5} className="text-white" />}
      </span>
      {children}
    </button>
  );
}

/** Groupe de cases à choix unique, branché sur une question de la fiche. */
export function Choix({
  k,
  options,
  fort,
  vertical,
}: {
  k: ChampTexte;
  options: Array<[string, ReactNode]>;
  fort?: boolean;
  vertical?: boolean;
}) {
  const { d, set } = useFiche();
  return (
    <div className={vertical ? 'flex flex-col items-start gap-1.5' : 'flex flex-wrap items-center gap-1.5'}>
      {options.map(([val, label]) => (
        <Case key={val} on={d[k] === val} fort={fort} onClick={() => set(k, (d[k] === val ? '' : val) as never)}>
          {label}
        </Case>
      ))}
    </div>
  );
}

/** Oui / Non — dans l'ordre du papier, qui varie d'une question à l'autre. */
export function OuiNon({ k, ordre = 'oui-non' }: { k: ChampTexte; ordre?: 'oui-non' | 'non-oui' }) {
  const oui: [string, string] = ['OUI', 'Oui'];
  const non: [string, string] = ['NON', 'Non'];
  return <Choix k={k} options={ordre === 'oui-non' ? [oui, non] : [non, oui]} />;
}

/** Une question à puce (▪) suivie de sa réponse Oui/Non. */
export function PuceOuiNon({ k, children, apres }: { k: ChampTexte; children: ReactNode; apres?: ReactNode }) {
  return (
    <Ligne className="pl-4">
      <span className="text-slate-400">▪</span>
      <span className="text-[13px] text-slate-800 min-w-[190px]">{children}</span>
      <OuiNon k={k} />
      {apres && <span className="text-[12px] font-semibold text-slate-600">{apres}</span>}
    </Ligne>
  );
}
